import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowUpDown, ArrowRight, Search, AlertTriangle, TrendingUp,
  Layers, Calculator, BookOpen, Landmark, Repeat,
} from 'lucide-react';
import BackOrHomeButton from '@/components/BackOrHomeButton';
import SiteDisclaimer from '@/components/SiteDisclaimer';
import { useSeoMeta } from '@/pages/learn/LearnPage';
import { fmtPct, fmtAum, fmtDate } from '@/components/coveredCall/ccFormat';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Canadian cross-listed dividend payers — /canadian-dividend-stocks
 *
 * The point of difference: every yield here is computed from the CAD
 * dividend converted to the USD the New York listing prices in. Sites
 * that skip that step publish these yields ~39% too high.
 */
const COLUMNS = [
  { key: 'ticker', label: 'Stock', align: 'left' },
  { key: 'sector', label: 'Sector', align: 'left' },
  { key: 'price', label: 'Price' },
  { key: 'dividend_yield', label: 'Yield (USD)' },
  { key: 'dividend_rate', label: 'Annual/share' },
  { key: 'dividend_cagr_5y', label: '5y growth' },
  { key: 'streak', label: 'Streak' },
];

export default function CanadianDividendStocks() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [sector, setSector] = useState('all');
  const [sort, setSort] = useState({ key: 'dividend_yield', dir: 'desc' });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/canadian-dividend-stocks`)
      .then((r) => { if (!cancelled) { setData(r.data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useSeoMeta({
    title: 'Canadian dividend stocks on the NYSE — yields in USD | DiviDrip',
    description: 'Canadian dividend payers cross-listed in New York — RY, TD, ENB, BCE, CNQ, FTS and more — with yields converted from the declared Canadian dollar dividend into the US dollars the NYSE listing trades in, plus the 15% withholding rules and the IRA exemption.',
    canonical: 'https://twylightcrow.com/canadian-dividend-stocks',
  });

  const rows = data?.rows || [];
  const s = data?.stats;

  const visible = useMemo(() => {
    const needle = q.trim().toUpperCase();
    const out = rows.filter((r) => {
      if (sector !== 'all' && r.sector !== sector) return false;
      if (!needle) return true;
      return r.ticker.includes(needle) || (r.name || '').toUpperCase().includes(needle);
    });
    const { key, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    return [...out].sort((a, b) => {
      const av = a[key]; const bv = b[key];
      if (['ticker', 'sector'].includes(key)) {
        return String(av || '').localeCompare(String(bv || '')) * mult;
      }
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (av - bv) * mult;
    });
  }, [rows, q, sector, sort]);

  const faqs = useMemo(() => (s ? buildFaqs(s, data) : []), [s, data]);

  useEffect(() => {
    if (!data) return;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question', name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    el.setAttribute('data-testid', 'canada-jsonld');
    document.head.appendChild(el);
    return () => el.remove();
  }, [data, faqs]);

  const toggleSort = (key) => setSort((st) => (
    st.key === key ? { key, dir: st.dir === 'desc' ? 'asc' : 'desc' }
      : { key, dir: ['ticker', 'sector'].includes(key) ? 'asc' : 'desc' }
  ));

  const selectSector = (next) => {
    setSector((prev) => (prev === next ? 'all' : next));
    document.querySelector('[data-testid="canada-table-section"]')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 space-y-4 animate-pulse" data-testid="canada-loading">
        <div className="h-10 w-2/3 bg-slate-200 dark:bg-slate-800 rounded" />
        <div className="h-40 bg-slate-200 dark:bg-slate-800 rounded-xl" />
      </div>
    );
  }

  if (!data || !s?.count) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12" data-testid="canada-error">
        <BackOrHomeButton />
        <h1 className="mt-6 text-3xl font-black text-slate-900 dark:text-slate-100">Data unavailable</h1>
        <SiteDisclaimer />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="canada-page">
      <BackOrHomeButton />

      <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-rose-600 dark:text-rose-400 font-semibold">
        <Landmark className="w-3.5 h-3.5" />
        <Link to="/dividend-stocks-by-sector" className="hover:underline" data-testid="canada-breadcrumb">Income research</Link>
      </div>

      <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight text-slate-900 dark:text-slate-100">
        Canadian dividend stocks, <span className="text-rose-600 dark:text-rose-400">priced in dollars you spend</span>
      </h1>
      <p className="mt-4 max-w-3xl text-base text-slate-600 dark:text-slate-400 leading-relaxed">
        {s.count} Canadian companies trade their ordinary shares in New York alongside Toronto —
        RY, ENB, BCE, CNQ, Fortis. They are not ADRs, there is no depositary and no ADR fee, and
        you buy them in your US brokerage account like any other listed stock.
      </p>
      <p className="mt-3 max-w-3xl text-base text-slate-700 dark:text-slate-300 leading-relaxed">
        <strong>The catch nobody prices in:</strong> {s.cad_declarers} of them declare the dividend
        in <strong>Canadian dollars</strong> while the New York listing trades in{' '}
        <strong>US dollars</strong>. Divide one by the other and the yield comes out about{' '}
        <strong>39% too high</strong> — Enbridge reads 7.55% instead of {fmtPct(5.52)}, Telus once
        read 16%. Every yield on this page is the CAD dividend converted at{' '}
        <strong>{s.fx_rate?.toFixed(4)} USD/CAD</strong>{s.fx_as_of && ` (${fmtDate(s.fx_as_of)})`}{' '}
        and then divided by the US price.
      </p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="canada-stats">
        <Stat label="Cross-listed payers" value={s.count} testId="canada-stat-count" />
        <Stat label="Median yield (USD)" value={fmtPct(s.median_yield)} testId="canada-stat-median" />
        <Stat
          label="Highest yield"
          value={`${s.top_yield_ticker} · ${fmtPct(s.top_yield_value)}`}
          testId="canada-stat-top"
        />
        <Stat
          label="Longest streak"
          value={s.streak_leader ? `${s.streak_leader.ticker} · ${s.streak_leader.streak} yrs` : '—'}
          hint={s.streak_leader?.name}
          testId="canada-stat-streak"
        />
      </div>

      <p className="mt-3 text-xs text-slate-500 dark:text-slate-400" data-testid="canada-coverage">
        {s.converted} yields are currency-converted here; the other {s.usd_declarers} — the
        Brookfield entities, Nutrien, Agnico Eagle, Restaurant Brands and friends — already
        declare in US dollars and pass through untouched.
        {!!s.flagged && ` ${s.flagged} row is flagged because its trailing rate hasn't caught up
        with a declared cut, and it is kept out of the headline figures.`}
        {!!s.recent_cuts && ` ${s.recent_cuts} ${s.recent_cuts === 1 ? 'name has' : 'names have'}
        declared a dividend cut and ${s.recent_cuts === 1 ? 'its' : 'their'} yield here already
        uses the newly declared payment rather than the trailing year.`}
        {!!s.recent_raises && ` ${s.recent_raises} ${s.recent_raises === 1 ? 'has' : 'have'}
        declared a raise, likewise applied immediately.`}
      </p>

      {/* Tax block — the reason this page exists twice over */}
      <section className="mt-12 p-6 rounded-xl border border-rose-200 dark:border-rose-900/60 bg-rose-50/40 dark:bg-rose-950/20" data-testid="canada-tax">
        <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
          Canada withholds 15% — unless the shares sit in your IRA
        </h2>
        <div className="mt-3 grid md:grid-cols-3 gap-4 text-sm">
          <div>
            <div className="font-bold text-slate-900 dark:text-slate-100">Taxable brokerage</div>
            <p className="mt-1 text-slate-600 dark:text-slate-400 leading-relaxed">
              15% is withheld at source under the US-Canada treaty, and you claim it back as a
              foreign tax credit on Form 1116 (or directly on Schedule 3 under the $300/$600 de
              minimis). Net cost: usually nothing, but you finance it until you file.
            </p>
          </div>
          <div>
            <div className="font-bold text-emerald-700 dark:text-emerald-400">IRA / 401(k) — 0%</div>
            <p className="mt-1 text-slate-600 dark:text-slate-400 leading-relaxed">
              Article XXI of the treaty exempts US retirement accounts, so a traditional IRA,
              Roth IRA or 401(k) generally receives the dividend gross. Canada is the rare
              foreign market that is <em>better</em> inside an IRA than out.
            </p>
          </div>
          <div>
            <div className="font-bold text-amber-700 dark:text-amber-500">Watch the exceptions</div>
            <p className="mt-1 text-slate-600 dark:text-slate-400 leading-relaxed">
              The exemption is for portfolio dividends. Canadian REIT and income-trust
              distributions can be treated differently, and a broker that hasn&apos;t filed the
              right paperwork may withhold anyway — check the 1042-S your broker issues.
            </p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link to="/learn/international-dividends" className="inline-flex items-center gap-1 font-semibold text-rose-700 dark:text-rose-400 hover:underline" data-testid="canada-tax-guide-1">
            <BookOpen className="w-3.5 h-3.5" /> ADRs, withholding tax & the foreign tax credit
          </Link>
          <Link to="/learn/foreign-ordinary-shares-tax" className="inline-flex items-center gap-1 font-semibold text-rose-700 dark:text-rose-400 hover:underline" data-testid="canada-tax-guide-2">
            <BookOpen className="w-3.5 h-3.5" /> Foreign ordinary shares & withholding
          </Link>
        </div>
      </section>

      {/* Currency impact — the part that isn't in the yield */}
      <section className="mt-12 p-6 rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/40 dark:bg-amber-950/10" data-testid="canada-fx-impact">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] font-bold text-amber-700 dark:text-amber-500">
          <Repeat className="w-3.5 h-3.5" /> What the exchange rate does to your income
        </div>
        <p className="mt-3 text-base text-slate-800 dark:text-slate-200 leading-relaxed">
          The board declares in Canadian dollars; your broker converts each payment on the day it
          lands. So your income moves with USD/CAD even when the company never touches its
          dividend — and it moves the <em>opposite</em> way to the headline rate you see quoted
          in Toronto.
        </p>
        {s.fx_change_1y != null ? (
          <p className="mt-3 text-base text-slate-800 dark:text-slate-200 leading-relaxed">
            Over the last twelve months the rate went{' '}
            <strong className={s.fx_change_1y >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}>
              {s.fx_change_1y >= 0 ? '+' : ''}{s.fx_change_1y}%
            </strong>{' '}
            ({(s.fx_rate / (1 + s.fx_change_1y / 100)).toFixed(4)} → {s.fx_rate?.toFixed(4)} USD
            per CAD). A holder who collected the same Canadian dividend all year therefore banked{' '}
            <strong>
              {Math.abs(s.fx_change_1y) < 1
                ? 'almost exactly the same in US dollars'
                : `${Math.abs(s.fx_change_1y)}% ${s.fx_change_1y > 0 ? 'more' : 'less'} in US dollars`}
            </strong>
            {Math.abs(s.fx_change_1y) < 1
              ? ' — a quiet year by this measure, which is not the norm.'
              : ' without the company changing anything.'}{' '}
            {Math.abs(s.fx_change_1y) >= 1 && (
              <>
                On $30,000 of Canadian holdings at the {fmtPct(s.median_yield)} median that is
                about{' '}
                <strong>
                  ${Math.abs(Math.round(30000 * (s.median_yield / 100) * (s.fx_change_1y / 100)))}
                </strong>{' '}
                a year of pure currency.
              </>
            )}
          </p>
        ) : (
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">
            We publish the current rate above; the year-ago comparison appears once the FX
            history has refreshed.
          </p>
        )}
        <p className="mt-3 text-xs text-slate-600 dark:text-slate-400">
          Two practical consequences. First, a Canadian dividend is not a fixed US-dollar income
          stream, so treat it as a slightly variable payer even when the payout is famously
          stable. Second, currency cuts both ways over a holding period long enough for dividend
          growth to matter — since 2014 USD/CAD has swung wide enough to move a US holder&apos;s
          income by more than most of these companies changed their payouts. Hedging it is
          possible and rarely worth the cost on a dividend position; knowing it is happening is
          free.
        </p>
      </section>

      {/* Sectors */}
      <section className="mt-12" data-testid="canada-sectors">
        <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
          Where the Canadian income is
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Tap a sector to filter the table below.
        </p>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.sector_rows.map((x) => (
            <button
              key={x.sector}
              onClick={() => selectSector(x.sector)}
              className={`text-left p-4 rounded-xl border transition-colors ${sector === x.sector
                ? 'border-rose-500 bg-rose-50/60 dark:bg-rose-950/20'
                : 'border-slate-200 dark:border-slate-800 hover:border-rose-400 dark:hover:border-rose-700'}`}
              data-testid={`canada-sector-${x.sector.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-bold text-slate-900 dark:text-slate-100">{x.sector}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400">{x.count} stocks</span>
              </div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                Median {fmtPct(x.median_yield)} · top {x.top_yield_ticker}
              </div>
              <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-rose-700 dark:text-rose-400">
                {sector === x.sector ? 'Showing these — tap to clear' : 'Show these'}
                <ArrowRight className="w-3 h-3" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* Cuts */}
      <section className="mt-12 grid md:grid-cols-3 gap-4 min-w-0" data-testid="canada-cuts">
        <CutCard icon={TrendingUp} title="Highest yield" rows={data.cuts.highest_yield} metric={(r) => fmtPct(r.dividend_yield)} testId="canada-cut-yield" />
        <CutCard icon={Repeat} title="Longest raise streak" rows={data.cuts.longest_streak} metric={(r) => `${r.streak} yrs`} testId="canada-cut-streak" />
        <CutCard icon={ArrowUpDown} title="Fastest 5y dividend growth" rows={data.cuts.fastest_growth} metric={(r) => fmtPct(r.dividend_cagr_5y, 1)} testId="canada-cut-growth" />
      </section>

      {/* Table */}
      <section className="mt-12" data-testid="canada-table-section">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg md:text-lg font-bold text-slate-900 dark:text-slate-100">
              {sector === 'all' ? `All ${s.count} Canadian payers` : `Canadian ${sector.toLowerCase()} payers`}
            </h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {visible.length} shown · click any column to sort
              {sector !== 'all' && (
                <button onClick={() => setSector('all')} className="ml-2 font-semibold text-rose-700 dark:text-rose-400 hover:underline" data-testid="canada-clear-filter">clear filter</button>
              )}
            </p>
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ticker or company…"
              className="pl-9 pr-3 py-2 w-64 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100"
              data-testid="canada-search"
            />
          </div>
        </div>

        <p className="mt-3 sm:hidden text-[11px] text-slate-500 dark:text-slate-400" data-testid="canada-scroll-hint">
          Swipe the table sideways to see yield, growth and streak →
        </p>

        <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
          <table className="w-full text-sm" data-testid="canada-table">
            <thead className="bg-slate-50 dark:bg-slate-900/60">
              <tr>
                {COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    onClick={() => toggleSort(c.key)}
                    className={`px-3 py-2.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 cursor-pointer select-none hover:text-rose-600 dark:hover:text-rose-400 whitespace-nowrap ${c.align === 'left' ? 'text-left' : 'text-right'}`}
                    data-testid={`canada-th-${c.key}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {c.label}
                      {sort.key === c.key && <ArrowUpDown className="w-3 h-3 text-rose-500" />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.ticker} className="border-t border-slate-100 dark:border-slate-800/70 hover:bg-slate-50 dark:hover:bg-slate-900/40" data-testid={`canada-row-${r.ticker}`}>
                  <td className="px-3 py-2.5">
                    <Link to={`/stock/${r.ticker}`} className="font-mono font-bold text-slate-900 dark:text-slate-100 hover:text-rose-600 dark:hover:text-rose-400">{r.ticker}</Link>
                    <div className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[260px]">{r.name}</div>
                  </td>
                  <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.sector || '—'}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-300 whitespace-nowrap">{r.price ? `$${r.price.toFixed(2)}` : '—'}</td>
                  <td className="px-3 py-2.5 text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                    {fmtPct(r.dividend_yield)}
                    {r.rate_basis === 'forward_cut' && (
                      <span
                        className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400 align-middle"
                        title={`Dividend cut ${r.change_pct}% — this yield already uses the newly declared payment, not the trailing year`}
                        data-testid={`canada-cut-${r.ticker}`}
                      >cut {r.change_pct}%</span>
                    )}
                    {r.rate_basis === 'forward_raise' && (
                      <span
                        className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400 align-middle"
                        title={`Dividend raised ${r.change_pct}% — this yield already uses the newly declared payment`}
                        data-testid={`canada-raise-${r.ticker}`}
                      >+{r.change_pct}%</span>
                    )}
                    {r.yield_suspect && (
                      <AlertTriangle
                        className="inline w-3 h-3 ml-1 text-amber-500"
                        title="Trailing rate — treat this yield as backward-looking"
                        data-testid={`canada-suspect-${r.ticker}`}
                      />
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-400 whitespace-nowrap" title={r.dividend_rate_local ? `Declared ${r.dividend_rate_local} ${r.currency} a year` : 'Declared in US dollars'}>
                    {r.dividend_rate ? `$${r.dividend_rate.toFixed(2)}` : '—'}
                    {r.dividend_rate_local && <span className="ml-1 text-[10px] text-slate-400">USD</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.dividend_cagr_5y == null ? '—' : fmtPct(r.dividend_cagr_5y, 1)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-400 whitespace-nowrap">{r.streak ? `${r.streak} yrs` : '—'}</td>
                </tr>
              ))}
              {!visible.length && (
                <tr><td colSpan={COLUMNS.length} className="px-3 py-8 text-center text-slate-500" data-testid="canada-empty">No Canadian payers match that filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
          Yield and annual dividend per share are in US dollars, converted from the declared
          amount at {s.fx_rate?.toFixed(4)} USD/CAD; hover a dividend to see the declared figure.
          Because the payment is set in Canadian dollars, your US-dollar income moves with the
          exchange rate even when the company never changes the dividend — a strengthening
          loonie raises your income, a weakening one cuts it.
        </p>
      </section>

      {/* Education */}
      <section className="mt-12 grid md:grid-cols-2 gap-4" data-testid="canada-education">
        <Note title="Cross-listed ordinary, not an ADR">
          An ADR is a US-traded receipt over foreign shares, issued by a depositary bank that
          usually takes a fee out of each dividend. These are the ordinary shares themselves,
          admitted to the NYSE — no depositary, no ADR fee, and the same voting rights a
          Toronto holder has.
        </Note>
        <Note title="Why the dividend moves when the company does nothing">
          The board declares in Canadian dollars. Your broker converts each payment on the day,
          so a 5% move in USD/CAD is a 5% move in your income. Over a decade that has swung both
          ways by more than most of these companies changed their payout.
        </Note>
        <Note title="Canadian banks and the streak myth">
          The big five have paid without interruption for well over a century, which gets
          reported as an unbroken raise streak. It isn&apos;t — they hold the dividend flat
          through downturns rather than cutting, so the streak counter resets while the cheque
          keeps arriving. Read the streak column with that in mind.
        </Note>
        <Note title="The 15% is not a loss">
          In a taxable account the withholding is a prepayment, recoverable as a foreign tax
          credit. What it really costs is timing and paperwork. In an IRA there is nothing to
          recover because, unusually, nothing is withheld.
        </Note>
      </section>

      <section className="mt-12 p-6 rounded-xl bg-gradient-to-br from-rose-50 to-amber-50 dark:from-slate-900 dark:to-slate-900/60 border border-slate-200 dark:border-slate-800" data-testid="canada-cta">
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 mb-3">Keep going</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/foreign-dividend-stocks" className="inline-flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-lg" data-testid="canada-cta-adr">
            <Landmark className="w-4 h-4" /> Foreign & ADR payers
          </Link>
          <Link to="/dividend-stocks-by-sector" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="canada-cta-sectors">
            <Layers className="w-4 h-4" /> Dividend stocks by sector
          </Link>
          <Link to="/dividend-reinvestment-calculator" className="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg" data-testid="canada-cta-calc">
            <Calculator className="w-4 h-4" /> Reinvestment calculator
          </Link>
        </div>
      </section>

      <section className="mt-12" data-testid="canada-faq">
        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-3">Frequently asked</h2>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-800 group" data-testid={`canada-faq-item-${i}`}>
              <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100 list-none flex items-start justify-between gap-3">
                <span>{f.q}</span>
                <ArrowRight className="w-4 h-4 mt-1 shrink-0 text-slate-400 group-open:rotate-90 transition-transform" />
              </summary>
              <p className="mt-3 text-sm text-slate-700 dark:text-slate-300 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <SiteDisclaimer />
    </div>
  );
}

/* ─── Sub-components ────────────────────────────────────────────── */

function Stat({ label, value, hint, testId }) {
  return (
    <div className="p-4 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/40 min-w-0" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">{label}</div>
      <div className="mt-1 text-2xl font-black tabular-nums text-slate-900 dark:text-slate-100 truncate">{value ?? '—'}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-500 dark:text-slate-400 truncate">{hint}</div>}
    </div>
  );
}

function Note({ title, children }) {
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800">
      <h3 className="font-bold text-slate-900 dark:text-slate-100">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{children}</p>
    </div>
  );
}

function CutCard({ icon: Icon, title, rows, metric, testId }) {
  if (!rows?.length) return null;
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 min-w-0 overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-slate-100">
        <Icon className="w-4 h-4 text-rose-600 dark:text-rose-400" /> {title}
      </div>
      <ol className="mt-3 space-y-1.5">
        {rows.map((r, i) => (
          <li key={r.ticker} className="flex items-center justify-between gap-3 text-sm min-w-0">
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] tabular-nums text-slate-400 w-4 shrink-0">{i + 1}</span>
              <Link to={`/stock/${r.ticker}`} className="font-mono font-bold text-slate-900 dark:text-slate-100 shrink-0 hover:text-rose-600">{r.ticker}</Link>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">{r.name}</span>
            </span>
            <span className="shrink-0 tabular-nums font-semibold text-slate-800 dark:text-slate-200">{metric(r)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

function buildFaqs(s, data) {
  const top = data.cuts.highest_yield?.[0];
  return [
    {
      q: 'Are Canadian stocks on the NYSE ADRs?',
      a: 'No. RY, TD, ENB, BCE and the rest of this list are cross-listed ordinary shares — the same security that trades in Toronto, admitted to the New York Stock Exchange directly. There is no depositary bank, no ADR ratio and no ADR service fee skimmed off the dividend. That is why they do not appear on our foreign/ADR page: technically they are not ADRs at all.',
    },
    {
      q: 'Why do other sites show a higher yield on Enbridge or BCE?',
      a: `Because the dividend is declared in Canadian dollars and the New York listing trades in US dollars. Divide the CAD dividend by the USD price and you overstate the yield by roughly 1 ÷ ${s.fx_rate?.toFixed(3)}, about 39% at today's rate. Enbridge reads 7.55% that way against ${fmtPct(5.52)} done properly. We convert the declared dividend at ${s.fx_rate?.toFixed(4)} USD/CAD first, which is why our numbers look lower and are right.`,
    },
    {
      q: 'How much tax is withheld on Canadian dividends?',
      a: '15% at source for a US resident under the US-Canada treaty, recoverable in a taxable account as a foreign tax credit. Inside an IRA, Roth IRA or 401(k) the treaty exempts US retirement accounts entirely, so nothing is normally withheld — Canada is the one major foreign market that is better held in a retirement account than a taxable one.',
    },
    {
      q: 'Which Canadian cross-listing yields the most right now?',
      a: `${top ? `${top.ticker} at ${fmtPct(top.dividend_yield)}` : 'See the table'}, against a median of ${fmtPct(s.median_yield)} across the ${s.count} names here. ${s.flagged ? 'One name is flagged and excluded from that headline because its trailing rate has not caught up with a declared cut.' : ''} Yields are USD-converted, so they are directly comparable with a US payer rather than flattered by the exchange rate.`,
    },
    {
      q: 'Does the exchange rate change my dividend income?',
      a: `Yes, and it is the part investors forget. The board sets the payment in Canadian dollars; your broker converts each one on the day it is paid. A 5% move in USD/CAD is a 5% move in the income you actually bank, in either direction, even if the company never changes its dividend.${s.fx_change_1y != null ? ` Over the past twelve months the rate moved ${s.fx_change_1y >= 0 ? '+' : ''}${s.fx_change_1y}%, so the same Canadian dividend paid ${Math.abs(s.fx_change_1y) < 1 ? 'almost exactly the same' : `${Math.abs(s.fx_change_1y)}% ${s.fx_change_1y > 0 ? 'more' : 'less'}`} in US dollars.` : ''} Over a decade currency has moved these payments more than most of the companies did.`,
    },
    {
      q: 'Do your yields react to a dividend cut straight away?',
      a: `Yes. The rate behind every yield here is the last payment the company actually declared, annualised — not the trailing twelve months. When Telus reset its quarterly dividend 55% lower in July 2026, effective with the October payment, a trailing calculation still showed a double-digit yield for months. Ours dropped to the new level the day the cut was declared, and the row carries a "cut" badge showing the size of the change.${s.recent_cuts ? ` ${s.recent_cuts === 1 ? 'One name' : `${s.recent_cuts} names`} on this page ${s.recent_cuts === 1 ? 'is' : 'are'} currently on a newly cut rate.` : ''} We keep the trailing figure for genuinely variable payers, where annualising one payment would be a guess rather than a forecast.`,
    },
    {
      q: 'Do Canadian banks really have 100-year dividend streaks?',
      a: 'They have paid dividends continuously for well over a century, which is not the same as raising them every year. The big five tend to hold the dividend flat through a downturn instead of cutting it, so a consecutive-increase counter resets while the payment itself never stops. Both facts are worth knowing, but only the second one is what most "streak" tables are measuring.',
    },
  ];
}
